import { TIMELINE_GUTTER_WIDTH, TIMELINE_START_HOUR, TIMELINE_END_HOUR } from '../../../lib/timeline';

const FOCUS_START_HOUR = 9;
const FOCUS_END_HOUR = 17;

interface TimelineGridProps {
  startHour?: number;
  endHour?: number;
  toPixel: (minutes: number) => number;
  pxPerHour?: number;
}

export function TimelineGrid({ startHour = TIMELINE_START_HOUR, endHour = TIMELINE_END_HOUR, toPixel }: TimelineGridProps) {
  const marks: Array<{ minutes: number; label: string }> = [];
  const firstVisibleHour = Number.isInteger(startHour) ? startHour : Math.ceil(startHour);
  for (let hour = firstVisibleHour; hour < endHour; hour++) {
    const minutes = hour * 60;
    const normalizedHour = ((hour % 24) + 24) % 24;
    let displayHour = normalizedHour;
    const period = normalizedHour >= 12 ? 'PM' : 'AM';
    if (displayHour === 0) displayHour = 12;
    else if (displayHour > 12) displayHour -= 12;
    marks.push({ minutes, label: `${displayHour} ${period}` });
  }

  const focusStartTop = toPixel(FOCUS_START_HOUR * 60);
  const focusEndTop = toPixel(FOCUS_END_HOUR * 60);
  const showFocusHours = FOCUS_END_HOUR > startHour && FOCUS_START_HOUR < endHour;

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute top-0 bottom-0 border-l border-border-subtle/80"
        style={{ left: TIMELINE_GUTTER_WIDTH }}
        aria-hidden="true"
      />
      {showFocusHours && focusEndTop > focusStartTop && (
        <div
          className="absolute right-0"
          style={{
            left: TIMELINE_GUTTER_WIDTH,
            top: Math.max(0, focusStartTop),
            height: Math.max(0, focusEndTop - Math.max(0, focusStartTop)),
          }}
          aria-hidden="true"
        >
          <div className="absolute left-0 right-0 top-0 border-t border-accent/25" />
          <div className="absolute left-0 right-0 bottom-0 border-t border-accent/25" />
        </div>
      )}
      {marks.map((mark) => {
        const top = toPixel(mark.minutes);
        const halfTop = toPixel(mark.minutes + 30);
        return (
          <div key={mark.minutes}>
            <div className="absolute left-0 right-0" style={{ top }}>
              <span className="absolute top-1.5 left-1 text-[10px] text-text-muted/55 font-mono select-none leading-none tracking-tight">
                {mark.label}
              </span>
              <div className="absolute left-0 right-0 border-t border-border-subtle/55" />
              <div className="absolute left-0 border-t border-border-default/70" style={{ width: TIMELINE_GUTTER_WIDTH }} />
            </div>
            {mark.minutes + 30 < endHour * 60 && (
              <div
                className="absolute left-0 right-0 border-t border-border-subtle/20"
                style={{ top: halfTop }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
